import {ShapeCommand} from '../commands.template';
import {
  IActJson,
  IFlowlineJson,
  IImageJson,
  IStickerJson,
  ITextJson
} from '../../diagram-data/diagram-json-handler.service';
import {ICopyData} from '../../core/services/copy-cells.service';
import {UpdateGroup} from '../../diagram-data/diagram-update.service';
import {v1 as uuid} from 'uuid';

export class PasteCommand extends ShapeCommand {
  protected services: any;
  private vertexSpawners: Array<(group: UpdateGroup) => any> = [];
  private vertexDespawners: Array<(group: UpdateGroup) => any> = [];
  private firstPaste: boolean;
  private readonly idMap: number[] = [];

  constructor(data) {
    super(data);
    this.firstPaste = true;
  }

  public execute() {
    super.execute();

    if (this.firstPaste) {
      this.spawnVertexes(this.object);
      this.firstPaste = false;
    } else {
      this.respawnVertexes();
    }
  }

  public reverse() {
    super.reverse();
    this.despawnVertexes();
  }

  private spawnVertexes(data: ICopyData) {
    const graph = this.services.registrySvc.getGraph();
    const group: UpdateGroup = this.services.diagramUpdateSvc.group();
    const bookedActNumbers = [];
    const bookedTextNumbers = [];
    const newCells = [];

    /** Order is important ! **/
    graph.getModel().beginUpdate();
    data.activities.forEach(act => newCells.push(this.spawnActivity(act, group, bookedActNumbers)));
    data.flowlines.forEach(fl => newCells.push(this.spawnFlowline(fl, group)));
    data.texts.forEach(text => newCells.push(this.spawnText(text, group, bookedTextNumbers)));
    data.images.forEach(image => newCells.push(this.spawnImage(image, group)));
    data.stickers.forEach(sticker => newCells.push(this.spawnSticker(sticker, group)));
    group.sendGroup();
    graph.getModel().endUpdate();
    graph.setSelectionCells(newCells);
  }

  private respawnVertexes() {
    const group: UpdateGroup = this.services.diagramUpdateSvc.group();
    const graph = this.services.registrySvc.getGraph();
    const newCells = [];

    graph.getModel().beginUpdate();
    this.vertexSpawners.forEach(fn => newCells.push(fn(group)));
    group.sendGroup();
    graph.getModel().endUpdate();
    graph.setSelectionCells(newCells);
  }

  private despawnVertexes() {
    const group: UpdateGroup = this.services.diagramUpdateSvc.group();
    const graph = this.services.registrySvc.getGraph();
    const cellsToDespawn = [];

    this.vertexDespawners.forEach(fn => cellsToDespawn.push(fn(group)));
    graph.removeCells(cellsToDespawn, false);
    group.sendGroup();
  }

  private spawnActivity(actJson: IActJson, updGroup: UpdateGroup, bookeadActNumbers: number[]) {
    const shapeSpawner = this.services.shapeSvc;
    const graph = this.services.registrySvc.getGraph();
    const graphHelper = this.services.graphHelperSvc;

    const spawnFn = (act: IActJson, group) => {
      const vertex = shapeSpawner.createActivity(act);
      const cell = graph.addCell(vertex, graphHelper.activityParent());

      if (typeof act.mxKey === 'undefined') {
        act.mxKey = Number(cell.id);
        this.services.angCompSvc.setCache(act.mxKey, cell.comp);
        cell.comp.instance.mxKey = Number(cell.id);
      }

      const requestData = restructActivity(act, act.mxKey);

      group.add({createActivity: requestData});

      return cell;
    };

    const despawnFn = (act, group) => {
      const cell = graph.getModel().getCell(act.mxKey);

      group.add({deleteActivity: {mxKey: act.mxKey}});

      return cell;
    };

    const oldKey = actJson.mxKey;

    bookeadActNumbers.unshift(graphHelper.getFreeNumber(2, bookeadActNumbers));
    actJson.number = bookeadActNumbers[0];
    delete actJson.mxKey;
    const resultCell = spawnFn(actJson, updGroup); // mutates actJson

    this.idMap[oldKey] = actJson.mxKey;
    this.vertexSpawners.push(vertexSpawnerFactory(actJson, spawnFn));
    this.vertexDespawners.push(vertexSpawnerFactory(actJson, despawnFn));

    return resultCell;
  }

  private spawnFlowline(flJson: IFlowlineJson, updGroup: UpdateGroup) {
    const shapeSpawner = this.services.shapeSvc;
    const graph = this.services.registrySvc.getGraph();
    const graphHelper = this.services.graphHelperSvc;

    const spawnFn = (fl: IFlowlineJson, group) => {
      let source = void 0;
      let target = void 0;

      if (this.firstPaste) {
        delete fl.connector;
        delete fl.terminator;

        if (fl.srcId && this.idMap[fl.srcId]) {
          fl.srcId = this.idMap[fl.srcId];
        } else {
          delete fl.srcId;
        }

        if (fl.tgtId && this.idMap[fl.tgtId]) {
          fl.tgtId = this.idMap[fl.tgtId];
        } else {
          delete fl.tgtId;
        }
      }

      if (fl.srcId) {
        source = graph.getModel().getCell(fl.srcId);
      }

      if (fl.tgtId) {
        target = graph.getModel().getCell(fl.tgtId);
      }

      const vertex = shapeSpawner.createFlowline(
        fl,
        true,
        source,
        target,
        graphHelper.flowlineParent()
      );
      const cell = graph.addCell(vertex, graphHelper.flowlineParent());

      cell.skipUndo = true;

      if (typeof fl.mxKey === 'undefined') {
        fl.mxKey = Number(cell.id);
      }

      const requestData = restructFlowline(fl, cell.id);

      group.add({createFlowline: requestData});

      return cell;
    };

    const despawnFn = (fl, group) => {
      const cell = graph.getModel().getCell(fl.mxKey);

      group.add({deleteFlowline: {mxKey: fl.mxKey}});

      return cell;
    };

    delete flJson.mxKey;
    const resultCell = spawnFn(flJson, updGroup);

    this.vertexSpawners.push(vertexSpawnerFactory(flJson, spawnFn));
    this.vertexDespawners.push(vertexSpawnerFactory(flJson, despawnFn));

    return resultCell;
  }

  private spawnText(textJson: ITextJson, updGroup: UpdateGroup, bookedTextNumbers: number[]) {
    const shapeSpawner = this.services.shapeSvc;
    const graph = this.services.registrySvc.getGraph();
    const graphHelper = this.services.graphHelperSvc;

    const spawnFn = (text: ITextJson, group) => {
      const vertex = shapeSpawner.createText(text, graphHelper.textParent());
      const cell = graph.addCell(vertex, graphHelper.textParent());

      if (typeof text.mxKey === 'undefined') {
        text.mxKey = Number(cell.id);
      }

      const requestData = restructText(text, text.mxKey);

      group.add({createText: requestData});

      return cell;
    };

    const despawnFn = (text, group) => {
      const cell = graph.getModel().getCell(text.mxKey);

      group.add({deleteText: {mxKey: text.mxKey}});

      return cell;
    };

    bookedTextNumbers.unshift(graphHelper.getFreeNumber(4, bookedTextNumbers));
    textJson.number = bookedTextNumbers[0];
    delete textJson.mxKey;
    const resultCell = spawnFn(textJson, updGroup);

    this.vertexSpawners.push(vertexSpawnerFactory(textJson, spawnFn));
    this.vertexDespawners.push(vertexSpawnerFactory(textJson, despawnFn));

    return resultCell;
  }

  private spawnImage(imageJson: IImageJson, updGroup: UpdateGroup) {
    const shapeSpawner = this.services.shapeSvc;
    const graph = this.services.registrySvc.getGraph();
    const graphHelper = this.services.graphHelperSvc;
    const jsonHandler = this.services.jsonHandlerSvc;

    const spawnFn = (image: IImageJson, group) => {
      checkAndReplaceImageUrl(image, jsonHandler.get().images); // @TODO: add backup URL
      const vertex = shapeSpawner.createImage(image, image.imageResized, graphHelper.imageParent());
      const cell = graph.addCell(vertex, graphHelper.imageParent());

      if (typeof image.mxKey === 'undefined') {
        image.mxKey = Number(cell.id);
      }

      const requestData = restructImage(image, image.mxKey);

      group.add({createImage: requestData});

      return cell;
    };

    const despawnFn = (image, group) => {
      const cell = graph.getModel().getCell(image.mxKey);

      group.add({deleteImage: {mxKey: image.mxKey}});

      return cell;
    };

    delete imageJson.mxKey;
    const resultCell = spawnFn(imageJson, updGroup);

    this.vertexSpawners.push(vertexSpawnerFactory(imageJson, spawnFn));
    this.vertexDespawners.push(vertexSpawnerFactory(imageJson, despawnFn));

    return resultCell;
  }

  private spawnSticker(stickerJson: IStickerJson, updGroup: UpdateGroup) {
    const shapeSpawner = this.services.shapeSvc;
    const graph = this.services.registrySvc.getGraph();
    const graphHelper = this.services.graphHelperSvc;

    const spawnFn = (sticker: IStickerJson, group) => {
      const vertex = shapeSpawner.createSticker(sticker, graphHelper.stickerParent());
      const cell = graph.addCell(vertex, graphHelper.stickerParent());

      if (typeof sticker.mxKey === 'undefined') {
        sticker.mxKey = Number(cell.id);
      }

      cell.tags = sticker.tags || [];
      const requestData = restructSticker(sticker, sticker.mxKey);

      group.add({createNote: requestData});

      return cell;
    };

    const despawnFn = (sticker, group) => {
      const cell = graph.getModel().getCell(sticker.mxKey);

      group.add({deleteNote: {mxKey: sticker.mxKey}});

      return cell;
    };

    delete stickerJson.mxKey;
    const resultCell = spawnFn(stickerJson, updGroup);

    this.vertexSpawners.push(vertexSpawnerFactory(stickerJson, spawnFn));
    this.vertexDespawners.push(vertexSpawnerFactory(stickerJson, despawnFn));

    return resultCell;
  }
}

/**
 * Utility functions
 * **/

function restructActivity(json: IActJson, newId: number): IActJson {
  let {
    attachments = [],
    refModelAttachments = [],
    requirementAttachments = [],
    storyAttachments = [],
    orgModelAttachments = []
  } = json; // only replaces undefined values, falsey statements are ignored
  const {
    resources = [],
    imageUrl,
    customStyle,
    style,
    number,
    text,
    position,
    image,
    container,
    footerText,
    attributes,
    linkedDiagram,
    attributePadding
  } = json;

  if (Array.isArray(attachments)) {
    attachments = attachments.map(restructAttachment(newId));
  }

  if (Array.isArray(refModelAttachments)) {
    refModelAttachments = refModelAttachments.map(restructRefModelAttachment(newId));
  }

  if (Array.isArray(requirementAttachments)) {
    requirementAttachments = requirementAttachments.map(restructRequirementAttachment(newId));
  }

  if (Array.isArray(storyAttachments)) {
    storyAttachments = storyAttachments.map(restructStoryAttachment(newId));
  }

  if (Array.isArray(orgModelAttachments)) {
    orgModelAttachments = orgModelAttachments.map(restructOrgModelAttachment(newId));
  }

  return {
    mxKey: newId,
    resources,
    attachments,
    refModelAttachments,
    orgModelAttachments,
    number,
    text,
    position: {
      x: position.x,
      y: position.y,
      w: position.w,
      h: position.h
    },
    image,
    imageUrl: imageUrl ? imageUrl : void 0,
    customStyle,
    style,
    requirementAttachments,
    storyAttachments,
    attributes,
    footerText,
    container,
    linkedDiagram,
    attributePadding
  };
}

function restructAttachment(newId: number) {
  return ({
    mxKey,
    type,
    groups,
    title,
    value,
    dataStructureDefinition,
    urlEntry,
    savedOnS3,
    noteProItems
  }) => {
    const newAtt: any = {
      itemMxKey: newId,
      mxKey,
      type,
      groups,
      title,
      value: savedOnS3 ? uuid() : value,
      dataStructureDefinition,
      savedOnS3,
      isCopyPaste: true,
      copySource: savedOnS3 ? value : null,
      noteProItems
    };

    if (type === 'url_entry') {
      newAtt.urlEntry = urlEntry;
    }

    return newAtt;
  };
}

function restructRefModelAttachment(newId: number) {
  return ({
    mxKey,
    type,
    title,
    groups,
    refModel,
    refModelNode,
    description,
    refModelName,
    nodePath,
    externalUrl,
    nodeDescription
  }) => {
    return {
      itemMxKey: newId,
      mxKey,
      type,
      title,
      groups,
      refModel,
      refModelNode,
      description,
      refModelName,
      nodePath,
      externalUrl,
      nodeDescription
    };
  };
}

function restructOrgModelAttachment(newId: number) {
  return ({
    mxKey,
    type,
    title,
    groups,
    sfNode,
    nodeDescription,
    nodeSummary,
    linkDisplayName,
    description,
    refModelName,
    nodePath,
    externalUrl,
    nodeObjectParent,
    objectType,
    nodeApiName,
    displayInUserHelp,
    displayInUserHelpClassic,
    pushedAsSfHelpDate
  }) => {
    return {
      itemMxKey: newId,
      mxKey,
      type,
      title,
      groups,
      sfNode,
      nodeDescription,
      nodeSummary,
      linkDisplayName,
      description,
      refModelName,
      nodePath,
      externalUrl,
      nodeObjectParent,
      objectType,
      nodeApiName,
      displayInUserHelp,
      displayInUserHelpClassic,
      pushedAsSfHelpDate
    };
  };
}

function restructRequirementAttachment(newId: number) {
  return ({mxKey, type, title, requirement, description, groups, reqId, reqReason, reqDetails}) => {
    const requirementId = requirement.id || requirement;

    return {
      itemMxKey: newId,
      mxKey,
      type,
      title,
      requirement: requirementId,
      description,
      groups,
      reqId,
      reqReason,
      reqDetails
    };
  };
}

function restructStoryAttachment(newId: number) {
  return ({mxKey, type = 'story', title, description, groups, story, storyId}) => {
    const storyAttachmentId = story.id || story;

    return {
      itemMxKey: newId,
      mxKey,
      type,
      title,
      description,
      groups,
      story: storyAttachmentId,
      storyId
    };
  };
}

function restructText(json: ITextJson, newId: number): ITextJson {
  const {number, text, customStyle, style, position, link} = json;

  return {
    mxKey: newId,
    number,
    text,
    customStyle,
    style,
    position: {
      x: position.x,
      y: position.y,
      w: position.w,
      h: position.h,
      rotation: position.rotation
    },
    link
  };
}

function restructSticker(json: IStickerJson, newId: number): IStickerJson {
  const {text, paperClass, position, tags} = json;

  return {
    mxKey: newId,
    text,
    paperClass,
    position: {
      x: position.x,
      y: position.y,
      w: position.w,
      h: position.h,
      rotation: position.rotation
    },
    tags
  };
}

function restructFlowline(json: IFlowlineJson, newId: number): IFlowlineJson {
  const {
    text,
    srcType,
    flForm,
    tgtType,
    waypoints,
    position,
    srcPosition,
    tgtPosition,
    customStyle,
    style,
    srcId,
    tgtId,
    terminator,
    connector,
    lineTexts
  } = json;

  if (terminator) {
    terminator.flowlineMxKey = newId;
  }

  if (connector) {
    connector.flowlineMxKey = newId;
  }

  return {
    mxKey: newId,
    text,
    srcType,
    flForm,
    tgtType,
    waypoints,
    position,
    srcPosition,
    tgtPosition,
    customStyle,
    style,
    srcId,
    tgtId,
    connector,
    terminator,
    lineTexts
  };
}

function restructImage(json: IImageJson, newId: number): IImageJson {
  const {uuid, position, link} = json;

  return {
    mxKey: newId,
    copyImage: true,
    uuid,
    position: {
      x: position.x,
      y: position.y,
      w: position.w,
      h: position.h,
      r: position.r
    },
    link
  };
}

function checkAndReplaceImageUrl(image: IImageJson, allImages: IImageJson[]) {
  if (!image.imageResized) {
    return;
  }

  const virtual = new Image();

  virtual.src = image.imageResized;

  if (!(virtual.complete || virtual.width + virtual.height > 0)) {
    image.imageResized = '';

    if (Array.isArray(allImages)) {
      const newImg = allImages.find(img =>
        Boolean(Number(img.mxKey) === Number(image.mxKey) && img.imageResized)
      );

      if (newImg) {
        image.imageResized = newImg.imageResized;
      }
    }
  }
}

function vertexSpawnerFactory<T>(json: T, cb: (json: T, group: UpdateGroup) => any) {
  return group => cb(json, group);
}
